import {
  ADMIN_USER_LIST,
  ADMIN_USER_CREATE,
  ADMIN_USER_EDIT,
  ADMIN_USER_UPDATE,
  ADMIN_USER_DELETE,
  ADMIN_USER_RESTORE,
} from "../../routes/routes";
import {
  COUNTRY_ADMIN_USER_CREATED,
  COUNTRY_ADMIN_USER_UPDATED,
  COUNTRY_ADMIN_USER_DELETED,
  STATE_ADMIN_USER_CREATED,
  STATE_ADMIN_USER_UPDATED,
  STATE_ADMIN_USER_DELETED,
  CITY_ADMIN_USER_CREATED,
  CITY_ADMIN_USER_UPDATED,
  CITY_ADMIN_USER_DELETED,
  REGION_ADMIN_USER_CREATED,
  REGION_ADMIN_USER_UPDATED,
  REGION_ADMIN_USER_DELETED,
  CHAPTER_ADMIN_USER_CREATED,
  CHAPTER_ADMIN_USER_UPDATED,
  CHAPTER_ADMIN_USER_DELETED,
} from "../../constant/constant";

/** Who can see each geo filter/column on the list (same as existing admin lists). */
export const GEO_LEVEL_FILTER_ROLES = {
  country: [1],
  state: [1, 6],
  city: [1, 6, 7],
  region: [1, 6, 7, 8],
  chapter: [1, 6, 7, 8, 2],
};

export const GEO_LEVEL_META = {
  country: {
    label: "Country",
    filterKey: "country_id",
    columnKey: "country_name",
    selectedKey: "country_id_selected",
    nameKey: "country_name",
  },
  state: {
    label: "State",
    filterKey: "state_id",
    columnKey: "state_name",
    selectedKey: "state_id_selected",
    nameKey: "state_name",
  },
  city: {
    label: "City",
    filterKey: "city_id",
    columnKey: "city_name",
    selectedKey: "city_id_selected",
    nameKey: "city_name",
  },
  region: {
    label: "Region",
    filterKey: "region_id",
    columnKey: "region_name",
    selectedKey: "region_id_selected",
    nameKey: "region_name",
  },
  chapter: {
    label: "Circle",
    filterKey: "chapter_id",
    columnKey: "chapter_name",
    selectedKey: "chapter_id_selected",
    nameKey: "chapter_name",
  },
};

const COMMON_API = {
  list: ADMIN_USER_LIST,
  create: ADMIN_USER_CREATE,
  edit: ADMIN_USER_EDIT,
  update: ADMIN_USER_UPDATE,
  delete: ADMIN_USER_DELETE,
  restore: ADMIN_USER_RESTORE,
};

export const ADMIN_TYPES = {
  country: {
    key: "country",
    label: "Country Admin",
    geoLevels: ["country"],
    access: { level: "state", label: "States" },
    visibleToRoles: [1],
    permissions: {
      list: "country-admins-list",
      create: "country-admins-create",
      edit: "country-admins-edit",
      delete: "country-admins-delete",
    },
    api: COMMON_API,
    paths: {
      create: "/admins/create",
      edit: (id) => `/admins/country/${id}/edit`,
    },
    toasts: {
      created: COUNTRY_ADMIN_USER_CREATED,
      updated: COUNTRY_ADMIN_USER_UPDATED,
      deleted: COUNTRY_ADMIN_USER_DELETED,
    },
  },
  state: {
    key: "state",
    label: "State Admin",
    geoLevels: ["country", "state"],
    access: { level: "city", label: "Cities" },
    visibleToRoles: [1, 6],
    permissions: {
      list: "state-admins-list",
      create: "state-admins-create",
      edit: "state-admins-edit",
      delete: "state-admins-delete",
    },
    api: COMMON_API,
    paths: {
      create: "/admins/create",
      edit: (id) => `/admins/state/${id}/edit`,
    },
    toasts: {
      created: STATE_ADMIN_USER_CREATED,
      updated: STATE_ADMIN_USER_UPDATED,
      deleted: STATE_ADMIN_USER_DELETED,
    },
  },
  city: {
    key: "city",
    label: "City Admin",
    geoLevels: ["country", "state", "city"],
    access: { level: "region", label: "Regions" },
    visibleToRoles: [1, 6, 7],
    permissions: {
      list: "city-admins-list",
      create: "city-admins-create",
      edit: "city-admins-edit",
      delete: "city-admins-delete",
    },
    api: COMMON_API,
    paths: {
      create: "/admins/create",
      edit: (id) => `/admins/city/${id}/edit`,
    },
    toasts: {
      created: CITY_ADMIN_USER_CREATED,
      updated: CITY_ADMIN_USER_UPDATED,
      deleted: CITY_ADMIN_USER_DELETED,
    },
  },
  region: {
    key: "region",
    label: "Region Admin",
    geoLevels: ["country", "state", "city", "region"],
    access: { level: "chapter", label: "Circles" },
    visibleToRoles: [1, 6, 7, 8],
    permissions: {
      list: "region-admins-list",
      create: "region-admins-create",
      edit: "region-admins-edit",
      delete: "region-admins-delete",
    },
    api: COMMON_API,
    paths: {
      create: "/admins/create",
      edit: (id) => `/admins/region/${id}/edit`,
    },
    toasts: {
      created: REGION_ADMIN_USER_CREATED,
      updated: REGION_ADMIN_USER_UPDATED,
      deleted: REGION_ADMIN_USER_DELETED,
    },
  },
  chapter: {
    key: "chapter",
    label: "Circle Admin",
    geoLevels: ["country", "state", "city", "region", "chapter"],
    access: null,
    visibleToRoles: [1, 6, 7, 8, 2],
    permissions: {
      list: "chapter-admins-list",
      create: "chapter-admins-create",
      edit: "chapter-admins-edit",
      delete: "chapter-admins-delete",
    },
    api: COMMON_API,
    paths: {
      create: "/admins/create",
      edit: (id) => `/admins/chapter/${id}/edit`,
    },
    toasts: {
      created: CHAPTER_ADMIN_USER_CREATED,
      updated: CHAPTER_ADMIN_USER_UPDATED,
      deleted: CHAPTER_ADMIN_USER_DELETED,
    },
  },
};

export const ADMIN_TYPE_KEYS = Object.keys(ADMIN_TYPES);

export const ADMIN_LIST_PERMISSIONS = ADMIN_TYPE_KEYS.map(
  (key) => ADMIN_TYPES[key].permissions.list
);

export const ADMIN_CREATE_PERMISSIONS = ADMIN_TYPE_KEYS.map(
  (key) => ADMIN_TYPES[key].permissions.create
);

export const ADMIN_EDIT_PERMISSIONS = ADMIN_TYPE_KEYS.map(
  (key) => ADMIN_TYPES[key].permissions.edit
);

export const canAccessAdminType = (typeKey, hasPermission, hasRoleFn) => {
  const config = ADMIN_TYPES[typeKey];
  if (!config) return false;
  return (
    hasPermission(config.permissions.list) &&
    hasRoleFn(config.visibleToRoles)
  );
};

export const canCreateAdminType = (typeKey, hasPermission, hasRoleFn) => {
  const config = ADMIN_TYPES[typeKey];
  if (!config) return false;
  return (
    hasPermission(config.permissions.create) &&
    hasRoleFn(config.visibleToRoles)
  );
};

export const canEditAdminType = (typeKey, hasPermission, hasRoleFn) => {
  const config = ADMIN_TYPES[typeKey];
  if (!config) return false;
  return (
    hasPermission(config.permissions.edit) &&
    hasRoleFn(config.visibleToRoles)
  );
};

export const getAllowedAdminTypes = (hasPermission, hasRoleFn) =>
  ADMIN_TYPE_KEYS.filter((key) =>
    canAccessAdminType(key, hasPermission, hasRoleFn)
  ).map((key) => ADMIN_TYPES[key]);

export const getCreatableAdminTypes = (hasPermission, hasRoleFn) =>
  ADMIN_TYPE_KEYS.filter((key) =>
    canCreateAdminType(key, hasPermission, hasRoleFn)
  ).map((key) => ADMIN_TYPES[key]);

export const ADMIN_TYPE_ID_MAP = {
  country: 6,
  state: 7,
  city: 8,
  region: 2,
  chapter: 3,
};

export const ADMIN_ROLE_TYPE_OPTIONS = [
  { value: 6, label: "Country Admin", key: "country" },
  { value: 7, label: "State Admin", key: "state" },
  { value: 8, label: "City Admin", key: "city" },
  { value: 2, label: "Region Admin", key: "region" },
  { value: 3, label: "Circle Admin", key: "chapter" },
];
